import { Mutex } from "async-mutex";
import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useMemo } from "react";
import { gracefullyDecompressJson } from "../compression/lazyStringCompression";
import { DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS } from "../config";
import { logger } from "../datadog/logger";
import { sendLoggingEvents, TrackingEvents } from "../events";
import { debounce, gracefullyParseJson } from "../utils";
import type {
  DataChannelMessage,
  RemoteDataDelegator,
  RemoteParticipant,
  RemoteParticipantMediaEventHandler,
  RemoteParticipantMutexEventMap,
} from "../video-sdk";
import {
  dataChannelAppStateAtom,
  getMaxDataChannelMessageSizeBytes,
  remoteMediaByParticipantIdAtom,
  remoteParticipantToDiagnosticInfo,
} from "../video-sdk";
import {
  addMediaToParticipant,
  remoteMediaDelagatorToDiagnosticInfo,
  removeMediaFromParticipant,
} from "../video-sdk/twilio";
import { useRemoteMediaEventListeners } from "./useRemoteMediaEventListeners";
import { gracefullyHandleMediaEvents } from "./utils";

export type UseRemoteDataProps = {
  remoteParticipant: RemoteParticipant;
  onTrackSubscribed?: RemoteParticipantMediaEventHandler;
  onTrackUnsubscribed?: RemoteParticipantMediaEventHandler;
  onMessageReceived?: (message: DataChannelMessage) => void;
};

const dataTrackEventMutex = new Mutex();

const sendDebouncedLoggingEvents = debounce(
  sendLoggingEvents,
  DATA_CHANNEL_MESSAGE_LOGGING_INTERVAL_MS,
);

async function gracefullyParseMessage(
  message: string,
): Promise<[DataChannelMessage, null] | [null, Error] | [null, null]> {
  const maxMessageSizeBytes = getMaxDataChannelMessageSizeBytes();
  return maxMessageSizeBytes
    ? await gracefullyDecompressJson<DataChannelMessage>(message)
    : [gracefullyParseJson<DataChannelMessage>(message), null];
}

function useGetRemoteDataChannelEventListeners({
  remoteParticipant,
  onTrackSubscribed,
  onTrackUnsubscribed,
  onMessageReceived,
}: UseRemoteDataProps) {
  const setRemoteTracks = useSetAtom(remoteMediaByParticipantIdAtom);
  const getDataTrackState = useAtomCallback(
    useCallback((get) => get(dataChannelAppStateAtom), []),
  );

  return useMemo(
    () => {
      if (!remoteParticipant) return;
      const handleReceivedMessage = async (message: string) => {
        const [parsedMessage, error] = await gracefullyParseMessage(message);
        if (!parsedMessage) {
          logger.error(
            "Unable to parse received message",
            {
              message,
            },
            ...(error ? [error] : []),
          );
          return null;
        }

        const participantState = getDataTrackState();

        sendDebouncedLoggingEvents(
          TrackingEvents.REMOTE_DATA_TRACK_MESSAGE_RECEIVED,
          {
            isRecording: participantState.isRecording,
            recordingState: participantState.recordingState,
            patientConsent: participantState.patient.consent,
            providerConsent: participantState.provider.consent,
            isWhiteboardMode: participantState.isWhiteboardMode,
          },
          {
            logLevel: "info",
            message: "Received a message on the data track (DEBOUNCED)",
          },
        );
        onMessageReceived?.(parsedMessage);
      };

      return gracefullyHandleMediaEvents(dataTrackEventMutex, {
        mediaSubscribed: (remoteDataDelagator, ...args) => {
          if (remoteDataDelagator.kind !== "data") return;
          onTrackSubscribed?.(remoteDataDelagator, ...args);
          setRemoteTracks((prev) =>
            addMediaToParticipant(
              prev,
              remoteParticipant.identity,
              remoteDataDelagator,
            ),
          );

          (remoteDataDelagator as RemoteDataDelegator).on(
            "message",
            handleReceivedMessage,
          );

          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_SUBSCRIBED,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteDataDelagator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: "info",
              message: "Remote participant data track subscribed",
            },
          );
        },

        mediaUnsubscribed: (remoteDataDelagator, ...args) => {
          if (remoteDataDelagator.kind !== "data") return;

          // On unsubscribe, the message event listener will not trigger anymore
          // regardless of the presence of the below line; this is just an extra
          // precaution to prevent memory leaks
          (remoteDataDelagator as RemoteDataDelegator).off(
            "message",
            handleReceivedMessage,
          );
          onTrackUnsubscribed?.(remoteDataDelagator, ...args);

          setRemoteTracks((prev) =>
            removeMediaFromParticipant(
              prev,
              remoteParticipant.identity,
              remoteDataDelagator,
            ),
          );

          sendLoggingEvents(
            TrackingEvents.REMOTE_TRACK_UNSUBSCRIBED,
            {
              ...remoteMediaDelagatorToDiagnosticInfo(remoteDataDelagator),
              ...remoteParticipantToDiagnosticInfo(remoteParticipant),
            },
            {
              logLevel: "info",
              message: "Remote participant data track unsubscribed",
            },
          );
        },
      } as RemoteParticipantMutexEventMap);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onMessageReceived,
      onTrackSubscribed,
      onTrackUnsubscribed,
      remoteParticipant,
      getDataTrackState,
    ],
  );
}

export function useRemoteData({
  remoteParticipant,
  ...otherProps
}: UseRemoteDataProps) {
  const remoteTrackEventListeners = useGetRemoteDataChannelEventListeners({
    remoteParticipant,
    ...otherProps,
  });

  //
  useRemoteMediaEventListeners({
    mediaKind: "data",
    remoteParticipant,
    remoteMediaEventListeners: remoteTrackEventListeners,
  });
}
